import { AutomationRunner } from '@dovefiniscono/core/automation/AutomationRunner';
import { GameProvider } from '@dovefiniscono/core/game/GameProvider';
import type { User } from '@dovefiniscono/shared';
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import type { CSSProperties } from 'react';
import { cx } from '../../lib/cx';
import { DataProvider } from '../../lib/data';
import { SECTIONS, useCurrentSection } from '../../lib/sections';
import { useSession } from '../../lib/session';
import { useShortcuts } from '../../lib/shortcuts';
import { Kbd } from '../../ui/Page';
import { Wordmark } from '../../ui/Wordmark';
import commandStyles from '../command/Command.module.css';
import { CommandPalette, openCommand } from '../command/CommandPalette';
import { GameToasts } from '../game/GameToasts';
import { LevelChip } from '../game/parts';
import { NotificationPlanner } from '../notifications/NotificationPlanner';
import styles from './AppShell.module.css';
import { SyncIndicator } from './SyncIndicator';

export function AppShell() {
  const { user } = useSession();
  // Right after "Esci" the session is gone one render before the guard moves to the login:
  // nothing to draw meanwhile, and nothing below may ask for the user.
  return user ? <SignedInShell user={user} /> : null;
}

function SignedInShell({ user }: { user: User }) {
  const navigate = useNavigate();
  const current = useCurrentSection();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  // 1–5 jump between sections: the numbers in the sidebar are the shortcuts.
  useShortcuts(
    Object.fromEntries(
      SECTIONS.map((section) => [section.key, () => void navigate({ to: section.to })]),
    ),
  );

  const activeIndex = current ? SECTIONS.indexOf(current) : -1;

  return (
    <DataProvider userId={user.id}>
      <GameProvider>
        <div className={styles.shell}>
          <aside className={styles.sidebar}>
            <Link to="/oggi" className={styles.brand} aria-label="dovefiniscono, vai a oggi">
              <Wordmark />
            </Link>
            <button
              type="button"
              className={cx(commandStyles.trigger, styles.command)}
              onClick={openCommand}
            >
              <span className={styles.commandLong}>Aggiungi o cerca</span>
              <span className={styles.commandShort}>Aggiungi</span>
              <Kbd>Ctrl K</Kbd>
            </button>
            {/* Drawn as a metro line: each section is a station, the marker slides to where you are. */}
            <nav
              aria-label="Sezioni"
              className={styles.navWrap}
              data-tone={current?.tone}
              style={{ '--active': Math.max(activeIndex, 0) } as CSSProperties}
            >
              {activeIndex >= 0 ? <span className={styles.marker} aria-hidden="true" /> : null}
              <ol className={styles.nav}>
                {SECTIONS.map((section) => (
                  <li key={section.to} data-tone={section.tone}>
                    <Link
                      to={section.to}
                      className={styles.navItem}
                      activeProps={{ className: styles.active }}
                      title={`${section.label} (${section.key})`}
                    >
                      <span className={styles.index}>0{section.key}</span>
                      <span className={styles.label}>{section.label}</span>
                    </Link>
                  </li>
                ))}
              </ol>
            </nav>
            <div className={styles.footer}>
              <div className={styles.deskOnly}>
                <LevelChip />
              </div>
              <Link
                to="/automazioni"
                className={cx(styles.footLink, styles.deskOnly)}
                activeProps={{ className: styles.footLinkActive }}
              >
                Automazioni
              </Link>
              <Link
                to="/revisione"
                className={cx(styles.footLink, styles.deskOnly)}
                activeProps={{ className: styles.footLinkActive }}
              >
                Revisione della settimana
              </Link>
              <SyncIndicator />
              <Link
                to="/impostazioni"
                className={styles.account}
                activeProps={{ className: styles.accountActive }}
              >
                <span className={styles.accountName}>{user.displayName}</span>
                <span className={styles.accountHint}>Impostazioni</span>
              </Link>
            </div>
          </aside>
          <main className={styles.main} data-tone={current?.tone}>
            {/* Keyed by path so every page plays its entrance. */}
            <div key={pathname} className={styles.content}>
              <Outlet />
            </div>
          </main>
        </div>
        <GameToasts />
        <AutomationRunner />
        <NotificationPlanner />
        <CommandPalette />
      </GameProvider>
    </DataProvider>
  );
}
